import React, { ReactNode, useEffect, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { WithLoadingSpinner } from "@rentablo/components";
import { ChartWrapper } from "../common";
import { useChartOptions } from "./useChartOptions";

export interface SparklineChartProps {
  data: number[];
  color?: string;
  height?: number;
  width?: number;
  isLoading?: boolean;
  noDataMessage?: ReactNode;
  showNoDataMessage?: boolean;
}

export const SparklineChart = ({
  data,
  color,
  height = 67,
  width,
  isLoading = false,
  noDataMessage,
  showNoDataMessage = true,
}: SparklineChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const chartOptions = useChartOptions(data, color, height);

  useEffect(() => {
    chartRef.current?.chart?.reflow();
  }, [chartOptions]);

  if (isLoading) {
    return (
      <Box height={height} width={width}>
        <WithLoadingSpinner
          isLoading
          height="100%"
          width={width}
          spinnerWidth={25}
        >
          <div />
        </WithLoadingSpinner>
      </Box>
    );
  }

  if (!data.length && showNoDataMessage) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width={width ?? "100%"}
        height={height}
      >
        <Typography
          variant="body3"
          color={theme.palette.text.secondary}
          textAlign="center"
        >
          {noDataMessage ?? t("depotPage.chart.noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <ChartWrapper>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
        containerProps={{
          style: { width: width ?? "100%", height },
        }}
      />
    </ChartWrapper>
  );
};
